import React from 'react';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { calculateExponentialBackoff, MAX_RETRY } from './utils';

type NetworkError = {
	response: {
		status: number;
		headers: Headers & Record<string, string>;
	};
};

export const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			/**
			 *
			 * Custom retry policy respecting rate limits
			 *
			 * When the backend returns HTTP 429, we pause retries based on the
			 * `Retry-After` header or fall back to an exponential delay. This prevents
			 * hammering the API while still allowing the query to eventually succeed.
			 */
			retry: (failureCount: number, error: unknown) => {
				// don't retry if 500 error
				if ((error as NetworkError)?.response?.status == 500) {
					return false;
				}
				// stop retrying after MAX_RETRY failures
				return failureCount + 1 <= MAX_RETRY;
			},
			retryDelay: (failureCount: number, error: unknown) => {
				const status = (error as NetworkError)?.response?.status;

				// Honor the Retry-After header when rate limited
				if (status === 429) {
					const header =
						(error as NetworkError)?.response?.headers?.get?.('Retry-After') ||
						(error as NetworkError)?.response?.headers?.get?.('retry-after') ||
						(error as NetworkError)?.response?.headers?.['Retry-After'] ||
						(error as NetworkError)?.response?.headers?.['retry-after'];

					const seconds = Number(header);

					if (!Number.isNaN(seconds)) {
						return seconds * 1000;
					}

					return calculateExponentialBackoff(failureCount);
				}

				return calculateExponentialBackoff(failureCount);
			},
		},
		mutations: {
			retry: false,
		},
	},
});

export default function QueryProvider({
	children,
}: {
	children: React.ReactNode;
}) {
	return (
		<QueryClientProvider client={queryClient}>
			{children}
			<ReactQueryDevtools initialIsOpen={false} />
		</QueryClientProvider>
	);
}
